import { motion } from 'motion/react';
import { cn } from '../../lib/utils';

interface EmptyStateProps {
  /** Optional lucide icon node — rendered at `text-muted-foreground`, no background. */
  icon?: React.ReactNode;
  /** Optional mono eyebrow above the title. */
  eyebrow?: string;
  /** Serif heading — the invitation. */
  title: string;
  /** Short plain-language subtext. */
  description?: string;
  /** Primary action — pass a <Button> or similar. */
  action?: React.ReactNode;
  /** Additional class names for the wrapper. */
  className?: string;
}

/**
 * Shared empty / error / "all clear" presentation.
 *
 * Usage:
 *   <EmptyState
 *     icon={<BookOpen className="h-8 w-8" />}
 *     title="Sua estante está esperando."
 *     description="Todo livro começa aqui."
 *     action={<Button onClick={…}>Criar meu primeiro livro</Button>}
 *   />
 */
export function EmptyState({
  icon,
  eyebrow,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-16',
        className,
      )}
    >
      <div className="max-w-sm mx-auto flex flex-col items-center gap-3">
        {icon && (
          <div className="mb-1 text-muted-foreground">
            {icon}
          </div>
        )}
        {eyebrow && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {eyebrow}
          </p>
        )}
        <h3 className="font-serif text-xl text-foreground leading-snug">
          {title}
        </h3>
        {description && (
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
            {description}
          </p>
        )}
        {action && (
          <div className="mt-2">
            {action}
          </div>
        )}
      </div>
    </motion.div>
  );
}
